<script>
  import { onMount } from "svelte"
  import { user } from "../../stores/User"
  import { BASE_URL } from "../../stores/BASE_URL"
  import SinInformacion from "../Sin_informacion.svelte"
  import { Datatable } from "svelte-simple-datatables"
  import exportTxt from "../../services/exportTxt.js"
  import Spinner from "../Spinner.svelte"
  import { navigate } from "svelte-routing"

  export let ruc

  const nombresMeses = [
    "Ene",
    "Feb",
    "Mar",
    "Abr",
    "May",
    "Jun",
    "Jul",
    "Ago",
    "Sep",
    "Oct",
    "Nov",
    "Dic",
  ]
  const columnasExportacion = [
    ["RUC", "ruc"],
    ["Razon Social", "razonsocial"],
    ["Condición", "condicion"],
    ["Devengue", "devengue"],
    ["Documento", "documento"],
    ["Paterno", "paterno"],
    ["Materno", "materno"],
    ["Nombres", "nombres"],
    ["P1", "ingreso"],
    ["Nacimiento", "nacimiento"],
    ["Departamento", "departamento"],
    ["Provincia", "provincia"],
    ["Distrito", "distrito"],
  ]
  const settings = {
    sortable: true,
    pagination: true,
    rowsPerPage: 10,
    columnFilter: false,
    scrollY: false,
    blocks: {
      searchInput: false,
      paginationButtons: true,
      paginationRowCount: false,
    },
    labels: { previous: "Anterior", next: "Siguiente" },
  }

  let meses = []
  let value = "planilla1"
  let empresa = []
  let rows
  let loading = true
  let loadingMeses = true
  let downloading = false
  let error = ""
  let errorMeses = ""
  let montado = false
  let datosController
  let mesesController
  let descargaController

  $: puedeDescargar =
    $user?.permisos?.includes("planilla1") ||
    $user?.permisos?.includes("planilla2")

  $: if (montado && !loadingMeses && meses.length && ruc && value) {
    cargarDatos(ruc, value, $user?.session_key)
  }

  const buildUrl = (archivo, params, sessionKey) => {
    const query = new URLSearchParams({
      ...params,
      session_key: sessionKey ?? "",
    })
    return `${BASE_URL}${archivo}?${query}`
  }

  const obtenerRespuesta = async (url, signal) => {
    const response = await fetch(url, { signal })
    if (!response.ok) {
      throw new Error("No se pudo completar la consulta.")
    }
    return response.json()
  }

  const validarLista = (lista) => {
    if (!Array.isArray(lista)) {
      throw new Error("La respuesta recibida no tiene el formato esperado.")
    }
    return lista
  }

  const cargarDatos = async (rucSeleccionado, planilla, sessionKey) => {
    datosController?.abort()
    const controller = new AbortController()
    datosController = controller
    loading = true
    error = ""
    empresa = []

    try {
      const resp = await obtenerRespuesta(
        buildUrl(
          "empresa.php",
          {
            ruc: rucSeleccionado,
            ops: "planilla_datos",
            planilla,
          },
          sessionKey,
        ),
        controller.signal,
      )
      if (controller.signal.aborted) return

      if (resp.status === "error") {
        error = resp.data?.message || "No se pudo cargar la planilla."
        navigate("/", { replace: true })
        return
      }
      empresa = validarLista(resp.data)
    } catch (err) {
      if (!controller.signal.aborted) {
        console.error("Error al cargar la planilla:", err)
        error = "No se pudo cargar la planilla. Intenta nuevamente."
      }
    } finally {
      if (!controller.signal.aborted) loading = false
    }
  }

  const getMesesPlanilla = async () => {
    mesesController?.abort()
    const controller = new AbortController()
    mesesController = controller
    loadingMeses = true
    errorMeses = ""

    try {
      const resp = await obtenerRespuesta(
        buildUrl("info.php", { ops: "getMesesPlanilla" }, $user?.session_key),
        controller.signal,
      )
      if (controller.signal.aborted) return

      if (resp.status === "error") {
        errorMeses = resp.data?.message || "No se pudieron cargar los periodos."
        navigate("/", { replace: true })
        return
      }

      meses = validarLista(resp.data).filter((mes) => mes?.mes)
      if (!meses.some((mes) => mes.mes === value)) {
        value = meses[0]?.mes ?? ""
      }
    } catch (err) {
      if (!controller.signal.aborted) {
        console.error("Error al cargar los periodos de planilla:", err)
        errorMeses = "No se pudieron cargar los periodos. Intenta nuevamente."
      }
    } finally {
      if (!controller.signal.aborted) {
        loadingMeses = false
        if (errorMeses || !meses.length || !ruc) loading = false
      }
    }
  }

  const convertDevengueToMes = (devengue) => {
    const periodo = String(devengue ?? "").trim()
    const mes = Number(periodo.slice(4, 6))
    if (!/^\d{6}$/.test(periodo) || mes < 1 || mes > 12) {
      return periodo || "Sin periodo"
    }
    return `${nombresMeses[mes - 1]}-${periodo.slice(0, 4)}`
  }

  const descargarDatos = async () => {
    if (downloading || loading || loadingMeses || !puedeDescargar || !value)
      return

    const controller = new AbortController()
    descargaController = controller
    const rucSeleccionado = ruc
    const planilla = value
    downloading = true

    try {
      const resp = await obtenerRespuesta(
        buildUrl(
          "empresa.php",
          {
            ruc: rucSeleccionado,
            ops: "planilla_descargar",
            planilla,
          },
          $user?.session_key,
        ),
        controller.signal,
      )
      if (controller.signal.aborted) return

      if (resp.status === "error") {
        alert(resp.data?.message || "No se pudo descargar la planilla.")
        return
      }

      const registros = validarLista(resp.data)
      const limpiarCampo = (valor) =>
        String(valor ?? "").replace(/[\t\r\n]+/g, " ")
      const encabezado = columnasExportacion
        .map(([titulo]) => titulo)
        .join("\t")
      const lineas = registros.map((registro) =>
        columnasExportacion
          .map(([, campo]) => limpiarCampo(registro[campo]))
          .join("\t"),
      )
      const blob = new Blob(
        [encabezado, ...lineas.map((linea) => "\n" + linea)],
        {
          type: "text/plain;charset=utf-8",
        },
      )
      exportTxt(blob, `planilla_${rucSeleccionado}.txt`)
    } catch (err) {
      if (!controller.signal.aborted) {
        console.error("Error al descargar la planilla:", err)
        alert("No se pudo descargar la planilla. Intenta nuevamente.")
      }
    } finally {
      downloading = false
    }
  }

  onMount(() => {
    montado = true
    getMesesPlanilla()

    return () => {
      montado = false
      datosController?.abort()
      mesesController?.abort()
      descargaController?.abort()
    }
  })
</script>

<div class="row mb-3 g-3">
  <div class="col-12 sm:col-6 md:col-4 lg:col-3">
    <select
      class="form-control form-control-sm"
      aria-label="Periodo de planilla"
      bind:value
      disabled={loadingMeses || downloading || !meses.length}
    >
      {#if loadingMeses}
        <option {value}>Cargando periodos...</option>
      {:else if !meses.length}
        <option value="">Sin periodos disponibles</option>
      {:else}
        {#each meses as mes}
          <option value={mes.mes}>{convertDevengueToMes(mes.devengue)}</option>
        {/each}
      {/if}
    </select>
  </div>
  <div class="col-12 sm:col-6 md:col-8 lg:col-9 text-end">
    <button
      type="button"
      class="btn btn-sm btn-solid theme-primary"
      disabled={loading ||
        loadingMeses ||
        downloading ||
        !puedeDescargar ||
        !value ||
        !!error ||
        !!errorMeses}
      on:click={descargarDatos}
    >
      <i class="bi bi-download" />
      {downloading ? "Descargando..." : "Descargar"}
    </button>
  </div>
</div>

{#if errorMeses}
  <div class="alert theme-danger" role="alert">
    {errorMeses}
    <button
      type="button"
      class="btn btn-sm btn-outline theme-danger ms-3"
      on:click={getMesesPlanilla}
    >
      Reintentar
    </button>
  </div>
{:else if loading || loadingMeses}
  <Spinner />
{:else if error}
  <div class="alert theme-danger" role="alert">
    {error}
    <button
      type="button"
      class="btn btn-sm btn-outline theme-danger ms-3"
      on:click={() => cargarDatos(ruc, value, $user?.session_key)}
      >Reintentar</button
    >
  </div>
{:else if !empresa.length}
  <SinInformacion />
{:else}
  <Datatable {settings} data={empresa} bind:dataRows={rows}>
    <table class="table table-striped table-sm">
      <thead>
        <tr>
          <th class="fg-secondary fw-normal text-start" data-key="condicion"
            >Condición</th
          >
          <th class="fg-secondary fw-normal text-start" data-key="devengue"
            >Devengue</th
          >
          <th class="fg-secondary fw-normal text-start" data-key="documento"
            >Documento</th
          >
          <th class="fg-secondary fw-normal text-start" data-key="nombres"
            >Nombres</th
          >
          <th class="fg-secondary fw-normal text-start" data-key="tipo">Tipo</th
          >
          <th class="fg-secondary fw-normal text-end" data-key="ingreso">P1</th>
        </tr>
      </thead>
      <tbody>
        {#if rows}
          {#each $rows as row}
            <tr>
              <td>{row.condicion}</td>
              <td>{row.devengue}</td>
              <td>{row.documento}</td>
              <td>{row.nombres}</td>
              <td>{row.tipo ?? ""}</td>
              <td class="text-end">{row.ingreso}</td>
            </tr>
          {/each}
        {/if}
      </tbody>
    </table>
  </Datatable>
{/if}
